/**
 * Company Provider Form Modal Component
 * Modal for creating/editing company-provider (clinic) links
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CompanyProviderLink } from '../../../../types/company';

interface ProviderLinkFormData {
  providerId: string;
  startDate: string;
  endDate: string;
  notes: string;
}

const providerLinkSchema = yup.object({
  providerId: yup.string().required('Prestador é obrigatório'),
  startDate: yup.string().required('Data de início é obrigatória'),
  endDate: yup.string().notRequired(),
  notes: yup.string().notRequired(),
});

interface CompanyProviderFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProviderLinkFormData) => Promise<void>;
  link?: CompanyProviderLink;
  providers: Array<{ id: number; name: string }>;
  error?: string | null;
}

const CompanyProviderFormModal: React.FC<CompanyProviderFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  link,
  providers,
  error,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEditing = !!link;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProviderLinkFormData>({
    resolver: yupResolver(providerLinkSchema) as any,
    defaultValues: {
      providerId: '',
      startDate: '',
      endDate: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open && link) {
      reset({
        providerId: link.providerId?.toString() || '',
        startDate: link.startDate?.split('T')[0] || '',
        endDate: link.endDate?.split('T')[0] || '',
        notes: link.notes ?? '',
      });
    } else if (open && !link) {
      reset({
        providerId: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        notes: '',
      });
    }
  }, [open, link, reset]);

  const handleFormSubmit = async (data: ProviderLinkFormData) => {
    setSubmitLoading(true);
    try {
      await onSubmit(data);
      onClose();
    } catch (_err) {
      // Error handling done by parent
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Vínculo' : 'Vincular Prestador'}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Provider */}
            <Grid size={12}>
              <Controller
                name="providerId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.providerId} disabled={isEditing}>
                    <InputLabel>Prestador *</InputLabel>
                    <Select {...field} label="Prestador *">
                      <MenuItem value="">Selecione...</MenuItem>
                      {providers.map((provider) => (
                        <MenuItem key={provider.id} value={provider.id.toString()}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.providerId && (
                      <FormHelperText>{errors.providerId.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Start Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Data de Início *"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                  />
                )}
              />
            </Grid>

            {/* End Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Data de Fim"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                  />
                )}
              />
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Observações"
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={submitLoading}>
            {submitLoading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              'Salvar'
            ) : (
              'Vincular'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CompanyProviderFormModal;
